/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Tessera Backend API
 * Tessera backend application with Bearer token authentication and multi-tenant support
 */
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  InvalidateOptions,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { BodyType, ErrorType } from '../api-instance';
import { apiInstance } from '../api-instance';
import type {
  ApproveHanaViewReviewPathParameters,
  AssignUserToHanaViewPathParameters,
  GetBulkRemediatedUploadJobStatusPathParameters,
  GetHanaViewDagParams,
  GetHanaViewDagPathParameters,
  GetHanaViewPathParameters,
  GetHanaViewProcedureSqlParams,
  GetHanaViewProcedureSqlPathParameters,
  GetHanaViewWriteBackJobPathParameters,
  GetHanaViewXmlParams,
  GetHanaViewXmlPathParameters,
  GetSetupJobStatusPathParameters,
  ListHanaViewActivityParams,
  ListHanaViewActivityPathParameters,
  ListHanaViewsParams,
  ListHanaViewVersionsPathParameters,
  PublishHanaViewToSapPathParameters,
  RejectHanaViewReviewPathParameters,
  SearchSapViewsParams,
  StartHanaViewPathParameters,
  SubmitHanaViewForReviewPathParameters,
  TLBulkUploadRemediatedHanaViewsRequest,
  TLCalculationViewDag,
  TLHanaViewAssignmentRequest,
  TLHanaViewBulkApprovalRequest,
  TLHanaViewBulkApprovalResponse,
  TLHanaViewBulkAssignmentRequest,
  TLHanaViewBulkAssignmentResponse,
  TLHanaViewBulkReviewRequest,
  TLHanaViewBulkReviewResponse,
  TLHanaViewBulkUploadJobStatusResponse,
  TLHanaViewBulkUploadResponse,
  TLHanaViewBulkWriteBackRequest,
  TLHanaViewBulkWriteBackSubmitResponse,
  TLHanaViewImportRequest,
  TLHanaViewResponse,
  TLHanaViewReviewerAssignmentRequest,
  TLHanaViewReviewRejectRequest,
  TLHanaViewSetupJobStatusResponse,
  TLHanaViewSetupRequest,
  TLHanaViewSetupResponse,
  TLHanaViewUpdateRequest,
  TLHanaViewUploadRequest,
  TLHanaViewVersionResponse,
  TLHanaViewWriteBackJobStatusResponse,
  TLHanaViewXmlResponse,
  TLHTTPValidationError,
  TLPaginatedResponseHanaViewResponse,
  TLProcedureSqlResponse,
  TLSapRepositoryViewResponse,
  TLUploadRemediatedHanaViewXmlRequest,
  TLViewActivityResponse,
  UpdateHanaViewMetadataPathParameters,
  UploadRemediatedHanaViewXmlPathParameters,
} from './tessera-backend-api.schemas';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary List Hana Views
 */
export const listHanaViews = (
  params?: ListHanaViewsParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLPaginatedResponseHanaViewResponse>(
    { url: `/hana-views`, method: 'GET', params, signal },
    options,
  );
};

export const getListHanaViewsQueryKey = (params?: ListHanaViewsParams) => {
  return ['hana-views', ...(params ? [params] : [])] as const;
};

export const getListHanaViewsQueryOptions = <
  TData = Awaited<ReturnType<typeof listHanaViews>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListHanaViewsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listHanaViews>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListHanaViewsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listHanaViews>>> = ({ signal }) =>
    listHanaViews(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listHanaViews>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListHanaViewsQueryResult = NonNullable<Awaited<ReturnType<typeof listHanaViews>>>;
export type ListHanaViewsQueryError = ErrorType<TLHTTPValidationError>;

export function useListHanaViews<
  TData = Awaited<ReturnType<typeof listHanaViews>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: undefined | ListHanaViewsParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof listHanaViews>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listHanaViews>>,
          TError,
          Awaited<ReturnType<typeof listHanaViews>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListHanaViews<
  TData = Awaited<ReturnType<typeof listHanaViews>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListHanaViewsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listHanaViews>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listHanaViews>>,
          TError,
          Awaited<ReturnType<typeof listHanaViews>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListHanaViews<
  TData = Awaited<ReturnType<typeof listHanaViews>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListHanaViewsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listHanaViews>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List Hana Views
 */

export function useListHanaViews<
  TData = Awaited<ReturnType<typeof listHanaViews>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListHanaViewsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listHanaViews>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListHanaViewsQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary List Hana Views
 */
export const prefetchListHanaViewsQuery = async <
  TData = Awaited<ReturnType<typeof listHanaViews>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  params?: ListHanaViewsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listHanaViews>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getListHanaViewsQueryOptions(params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary List Hana Views
 */
export const invalidateListHanaViews = async (
  queryClient: QueryClient,
  params?: ListHanaViewsParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getListHanaViewsQueryKey(params) }, options);

  return queryClient;
};

/**
 * @summary Bulk Assign User To Hana Views
 */
export const bulkAssignUserToHanaViews = (
  tLHanaViewBulkAssignmentRequest: BodyType<TLHanaViewBulkAssignmentRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLHanaViewBulkAssignmentResponse>(
    {
      url: `/hana-views/bulk-assign`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLHanaViewBulkAssignmentRequest,
      signal,
    },
    options,
  );
};

export const getBulkAssignUserToHanaViewsQueryKey = (
  tLHanaViewBulkAssignmentRequest?: BodyType<TLHanaViewBulkAssignmentRequest>,
) => {
  return ['POST', 'hana-views', 'bulk-assign', tLHanaViewBulkAssignmentRequest] as const;
};

export const getBulkAssignUserToHanaViewsQueryOptions = <
  TData = Awaited<ReturnType<typeof bulkAssignUserToHanaViews>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLHanaViewBulkAssignmentRequest: BodyType<TLHanaViewBulkAssignmentRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkAssignUserToHanaViews>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getBulkAssignUserToHanaViewsQueryKey(tLHanaViewBulkAssignmentRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof bulkAssignUserToHanaViews>>> = ({ signal }) =>
    bulkAssignUserToHanaViews(tLHanaViewBulkAssignmentRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof bulkAssignUserToHanaViews>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type BulkAssignUserToHanaViewsQueryResult = NonNullable<Awaited<ReturnType<typeof bulkAssignUserToHanaViews>>>;
export type BulkAssignUserToHanaViewsQueryError = ErrorType<TLHTTPValidationError>;

export function useBulkAssignUserToHanaViews<
  TData = Awaited<ReturnType<typeof bulkAssignUserToHanaViews>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLHanaViewBulkAssignmentRequest: BodyType<TLHanaViewBulkAssignmentRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkAssignUserToHanaViews>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof bulkAssignUserToHanaViews>>,
          TError,
          Awaited<ReturnType<typeof bulkAssignUserToHanaViews>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useBulkAssignUserToHanaViews<
  TData = Awaited<ReturnType<typeof bulkAssignUserToHanaViews>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLHanaViewBulkAssignmentRequest: BodyType<TLHanaViewBulkAssignmentRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkAssignUserToHanaViews>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof bulkAssignUserToHanaViews>>,
          TError,
          Awaited<ReturnType<typeof bulkAssignUserToHanaViews>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useBulkAssignUserToHanaViews<
  TData = Awaited<ReturnType<typeof bulkAssignUserToHanaViews>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLHanaViewBulkAssignmentRequest: BodyType<TLHanaViewBulkAssignmentRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkAssignUserToHanaViews>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Bulk Assign User To Hana Views
 */

export function useBulkAssignUserToHanaViews<
  TData = Awaited<ReturnType<typeof bulkAssignUserToHanaViews>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLHanaViewBulkAssignmentRequest: BodyType<TLHanaViewBulkAssignmentRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkAssignUserToHanaViews>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getBulkAssignUserToHanaViewsQueryOptions(tLHanaViewBulkAssignmentRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Bulk Assign User To Hana Views
 */
export const prefetchBulkAssignUserToHanaViewsQuery = async <
  TData = Awaited<ReturnType<typeof bulkAssignUserToHanaViews>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLHanaViewBulkAssignmentRequest: BodyType<TLHanaViewBulkAssignmentRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkAssignUserToHanaViews>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getBulkAssignUserToHanaViewsQueryOptions(tLHanaViewBulkAssignmentRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Bulk Assign User To Hana Views
 */
export const invalidateBulkAssignUserToHanaViews = async (
  queryClient: QueryClient,
  tLHanaViewBulkAssignmentRequest: BodyType<TLHanaViewBulkAssignmentRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getBulkAssignUserToHanaViewsQueryKey(tLHanaViewBulkAssignmentRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Bulk Upload Remediated Hana Views
 */
export const bulkUploadRemediatedHanaViews = (
  tLBulkUploadRemediatedHanaViewsRequest: BodyType<TLBulkUploadRemediatedHanaViewsRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  const formData = new FormData();
  formData.append(`mapping_file`, tLBulkUploadRemediatedHanaViewsRequest.mapping_file);
  formData.append(`views_zip`, tLBulkUploadRemediatedHanaViewsRequest.views_zip);

  return apiInstance<TLHanaViewBulkUploadResponse>(
    {
      url: `/hana-views/bulk-remediated-upload`,
      method: 'POST',
      headers: { 'Content-Type': 'multipart/form-data' },
      data: formData,
      signal,
    },
    options,
  );
};

export const getBulkUploadRemediatedHanaViewsQueryKey = (
  tLBulkUploadRemediatedHanaViewsRequest?: BodyType<TLBulkUploadRemediatedHanaViewsRequest>,
) => {
  return ['POST', 'hana-views', 'bulk-remediated-upload', tLBulkUploadRemediatedHanaViewsRequest] as const;
};

export const getBulkUploadRemediatedHanaViewsQueryOptions = <
  TData = Awaited<ReturnType<typeof bulkUploadRemediatedHanaViews>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLBulkUploadRemediatedHanaViewsRequest: BodyType<TLBulkUploadRemediatedHanaViewsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkUploadRemediatedHanaViews>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getBulkUploadRemediatedHanaViewsQueryKey(tLBulkUploadRemediatedHanaViewsRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof bulkUploadRemediatedHanaViews>>> = ({ signal }) =>
    bulkUploadRemediatedHanaViews(tLBulkUploadRemediatedHanaViewsRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof bulkUploadRemediatedHanaViews>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type BulkUploadRemediatedHanaViewsQueryResult = NonNullable<
  Awaited<ReturnType<typeof bulkUploadRemediatedHanaViews>>
>;
export type BulkUploadRemediatedHanaViewsQueryError = ErrorType<TLHTTPValidationError>;

export function useBulkUploadRemediatedHanaViews<
  TData = Awaited<ReturnType<typeof bulkUploadRemediatedHanaViews>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLBulkUploadRemediatedHanaViewsRequest: BodyType<TLBulkUploadRemediatedHanaViewsRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkUploadRemediatedHanaViews>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof bulkUploadRemediatedHanaViews>>,
          TError,
          Awaited<ReturnType<typeof bulkUploadRemediatedHanaViews>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useBulkUploadRemediatedHanaViews<
  TData = Awaited<ReturnType<typeof bulkUploadRemediatedHanaViews>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLBulkUploadRemediatedHanaViewsRequest: BodyType<TLBulkUploadRemediatedHanaViewsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkUploadRemediatedHanaViews>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof bulkUploadRemediatedHanaViews>>,
          TError,
          Awaited<ReturnType<typeof bulkUploadRemediatedHanaViews>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useBulkUploadRemediatedHanaViews<
  TData = Awaited<ReturnType<typeof bulkUploadRemediatedHanaViews>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLBulkUploadRemediatedHanaViewsRequest: BodyType<TLBulkUploadRemediatedHanaViewsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkUploadRemediatedHanaViews>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Bulk Upload Remediated Hana Views
 */

export function useBulkUploadRemediatedHanaViews<
  TData = Awaited<ReturnType<typeof bulkUploadRemediatedHanaViews>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLBulkUploadRemediatedHanaViewsRequest: BodyType<TLBulkUploadRemediatedHanaViewsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkUploadRemediatedHanaViews>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getBulkUploadRemediatedHanaViewsQueryOptions(tLBulkUploadRemediatedHanaViewsRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Bulk Upload Remediated Hana Views
 */
export const prefetchBulkUploadRemediatedHanaViewsQuery = async <
  TData = Awaited<ReturnType<typeof bulkUploadRemediatedHanaViews>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLBulkUploadRemediatedHanaViewsRequest: BodyType<TLBulkUploadRemediatedHanaViewsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkUploadRemediatedHanaViews>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getBulkUploadRemediatedHanaViewsQueryOptions(tLBulkUploadRemediatedHanaViewsRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Bulk Upload Remediated Hana Views
 */
export const invalidateBulkUploadRemediatedHanaViews = async (
  queryClient: QueryClient,
  tLBulkUploadRemediatedHanaViewsRequest: BodyType<TLBulkUploadRemediatedHanaViewsRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getBulkUploadRemediatedHanaViewsQueryKey(tLBulkUploadRemediatedHanaViewsRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Get Bulk Remediated Upload Job Status
 */
export const getBulkRemediatedUploadJobStatus = (
  { jobId }: GetBulkRemediatedUploadJobStatusPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLHanaViewBulkUploadJobStatusResponse>(
    { url: `/hana-views/bulk-remediated-upload/${jobId}`, method: 'GET', signal },
    options,
  );
};

export const getGetBulkRemediatedUploadJobStatusQueryKey = ({
  jobId,
}: GetBulkRemediatedUploadJobStatusPathParameters) => {
  return ['hana-views', 'bulk-remediated-upload', jobId] as const;
};

export const getGetBulkRemediatedUploadJobStatusQueryOptions = <
  TData = Awaited<ReturnType<typeof getBulkRemediatedUploadJobStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { jobId }: GetBulkRemediatedUploadJobStatusPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getBulkRemediatedUploadJobStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetBulkRemediatedUploadJobStatusQueryKey({ jobId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getBulkRemediatedUploadJobStatus>>> = ({ signal }) =>
    getBulkRemediatedUploadJobStatus({ jobId }, requestOptions, signal);

  return { queryKey, queryFn, enabled: jobId !== null && jobId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getBulkRemediatedUploadJobStatus>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetBulkRemediatedUploadJobStatusQueryResult = NonNullable<
  Awaited<ReturnType<typeof getBulkRemediatedUploadJobStatus>>
>;
export type GetBulkRemediatedUploadJobStatusQueryError = ErrorType<TLHTTPValidationError>;

export function useGetBulkRemediatedUploadJobStatus<
  TData = Awaited<ReturnType<typeof getBulkRemediatedUploadJobStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetBulkRemediatedUploadJobStatusPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getBulkRemediatedUploadJobStatus>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getBulkRemediatedUploadJobStatus>>,
          TError,
          Awaited<ReturnType<typeof getBulkRemediatedUploadJobStatus>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetBulkRemediatedUploadJobStatus<
  TData = Awaited<ReturnType<typeof getBulkRemediatedUploadJobStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetBulkRemediatedUploadJobStatusPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getBulkRemediatedUploadJobStatus>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getBulkRemediatedUploadJobStatus>>,
          TError,
          Awaited<ReturnType<typeof getBulkRemediatedUploadJobStatus>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetBulkRemediatedUploadJobStatus<
  TData = Awaited<ReturnType<typeof getBulkRemediatedUploadJobStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetBulkRemediatedUploadJobStatusPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getBulkRemediatedUploadJobStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Bulk Remediated Upload Job Status
 */

export function useGetBulkRemediatedUploadJobStatus<
  TData = Awaited<ReturnType<typeof getBulkRemediatedUploadJobStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { jobId }: GetBulkRemediatedUploadJobStatusPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getBulkRemediatedUploadJobStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetBulkRemediatedUploadJobStatusQueryOptions({ jobId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Bulk Remediated Upload Job Status
 */
export const prefetchGetBulkRemediatedUploadJobStatusQuery = async <
  TData = Awaited<ReturnType<typeof getBulkRemediatedUploadJobStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { jobId }: GetBulkRemediatedUploadJobStatusPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getBulkRemediatedUploadJobStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetBulkRemediatedUploadJobStatusQueryOptions({ jobId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Bulk Remediated Upload Job Status
 */
export const invalidateGetBulkRemediatedUploadJobStatus = async (
  queryClient: QueryClient,
  { jobId }: GetBulkRemediatedUploadJobStatusPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetBulkRemediatedUploadJobStatusQueryKey({ jobId }) }, options);

  return queryClient;
};

/**
 * @summary Bulk Write Back Hana Views
 */
export const bulkWriteBackHanaViews = (
  tLHanaViewBulkWriteBackRequest: BodyType<TLHanaViewBulkWriteBackRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLHanaViewBulkWriteBackSubmitResponse>(
    {
      url: `/hana-views/bulk-write-back`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLHanaViewBulkWriteBackRequest,
      signal,
    },
    options,
  );
};

export const getBulkWriteBackHanaViewsQueryKey = (
  tLHanaViewBulkWriteBackRequest?: BodyType<TLHanaViewBulkWriteBackRequest>,
) => {
  return ['POST', 'hana-views', 'bulk-write-back', tLHanaViewBulkWriteBackRequest] as const;
};

export const getBulkWriteBackHanaViewsQueryOptions = <
  TData = Awaited<ReturnType<typeof bulkWriteBackHanaViews>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLHanaViewBulkWriteBackRequest: BodyType<TLHanaViewBulkWriteBackRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkWriteBackHanaViews>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getBulkWriteBackHanaViewsQueryKey(tLHanaViewBulkWriteBackRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof bulkWriteBackHanaViews>>> = ({ signal }) =>
    bulkWriteBackHanaViews(tLHanaViewBulkWriteBackRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof bulkWriteBackHanaViews>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type BulkWriteBackHanaViewsQueryResult = NonNullable<Awaited<ReturnType<typeof bulkWriteBackHanaViews>>>;
export type BulkWriteBackHanaViewsQueryError = ErrorType<TLHTTPValidationError>;

export function useBulkWriteBackHanaViews<
  TData = Awaited<ReturnType<typeof bulkWriteBackHanaViews>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLHanaViewBulkWriteBackRequest: BodyType<TLHanaViewBulkWriteBackRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkWriteBackHanaViews>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof bulkWriteBackHanaViews>>,
          TError,
          Awaited<ReturnType<typeof bulkWriteBackHanaViews>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useBulkWriteBackHanaViews<
  TData = Awaited<ReturnType<typeof bulkWriteBackHanaViews>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLHanaViewBulkWriteBackRequest: BodyType<TLHanaViewBulkWriteBackRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkWriteBackHanaViews>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof bulkWriteBackHanaViews>>,
          TError,
          Awaited<ReturnType<typeof bulkWriteBackHanaViews>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useBulkWriteBackHanaViews<
  TData = Awaited<ReturnType<typeof bulkWriteBackHanaViews>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLHanaViewBulkWriteBackRequest: BodyType<TLHanaViewBulkWriteBackRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkWriteBackHanaViews>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Bulk Write Back Hana Views
 */

export function useBulkWriteBackHanaViews<
  TData = Awaited<ReturnType<typeof bulkWriteBackHanaViews>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLHanaViewBulkWriteBackRequest: BodyType<TLHanaViewBulkWriteBackRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkWriteBackHanaViews>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getBulkWriteBackHanaViewsQueryOptions(tLHanaViewBulkWriteBackRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Bulk Write Back Hana Views
 */
export const prefetchBulkWriteBackHanaViewsQuery = async <
  TData = Awaited<ReturnType<typeof bulkWriteBackHanaViews>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLHanaViewBulkWriteBackRequest: BodyType<TLHanaViewBulkWriteBackRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkWriteBackHanaViews>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getBulkWriteBackHanaViewsQueryOptions(tLHanaViewBulkWriteBackRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Bulk Write Back Hana Views
 */
export const invalidateBulkWriteBackHanaViews = async (
  queryClient: QueryClient,
  tLHanaViewBulkWriteBackRequest: BodyType<TLHanaViewBulkWriteBackRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getBulkWriteBackHanaViewsQueryKey(tLHanaViewBulkWriteBackRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Get Hana View Write Back Job
 */
export const getHanaViewWriteBackJob = (
  { jobId }: GetHanaViewWriteBackJobPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLHanaViewWriteBackJobStatusResponse>(
    { url: `/hana-views/bulk-write-back/${jobId}`, method: 'GET', signal },
    options,
  );
};

export const getGetHanaViewWriteBackJobQueryKey = ({ jobId }: GetHanaViewWriteBackJobPathParameters) => {
  return ['hana-views', 'bulk-write-back', jobId] as const;
};

export const getGetHanaViewWriteBackJobQueryOptions = <
  TData = Awaited<ReturnType<typeof getHanaViewWriteBackJob>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { jobId }: GetHanaViewWriteBackJobPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getHanaViewWriteBackJob>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetHanaViewWriteBackJobQueryKey({ jobId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getHanaViewWriteBackJob>>> = ({ signal }) =>
    getHanaViewWriteBackJob({ jobId }, requestOptions, signal);

  return { queryKey, queryFn, enabled: jobId !== null && jobId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getHanaViewWriteBackJob>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetHanaViewWriteBackJobQueryResult = NonNullable<Awaited<ReturnType<typeof getHanaViewWriteBackJob>>>;
export type GetHanaViewWriteBackJobQueryError = ErrorType<TLHTTPValidationError>;

export function useGetHanaViewWriteBackJob<
  TData = Awaited<ReturnType<typeof getHanaViewWriteBackJob>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetHanaViewWriteBackJobPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getHanaViewWriteBackJob>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getHanaViewWriteBackJob>>,
          TError,
          Awaited<ReturnType<typeof getHanaViewWriteBackJob>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetHanaViewWriteBackJob<
  TData = Awaited<ReturnType<typeof getHanaViewWriteBackJob>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetHanaViewWriteBackJobPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getHanaViewWriteBackJob>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getHanaViewWriteBackJob>>,
          TError,
          Awaited<ReturnType<typeof getHanaViewWriteBackJob>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetHanaViewWriteBackJob<
  TData = Awaited<ReturnType<typeof getHanaViewWriteBackJob>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetHanaViewWriteBackJobPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getHanaViewWriteBackJob>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Hana View Write Back Job
 */

export function useGetHanaViewWriteBackJob<
  TData = Awaited<ReturnType<typeof getHanaViewWriteBackJob>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { jobId }: GetHanaViewWriteBackJobPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getHanaViewWriteBackJob>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetHanaViewWriteBackJobQueryOptions({ jobId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Hana View Write Back Job
 */
export const prefetchGetHanaViewWriteBackJobQuery = async <
  TData = Awaited<ReturnType<typeof getHanaViewWriteBackJob>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { jobId }: GetHanaViewWriteBackJobPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getHanaViewWriteBackJob>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetHanaViewWriteBackJobQueryOptions({ jobId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Hana View Write Back Job
 */
export const invalidateGetHanaViewWriteBackJob = async (
  queryClient: QueryClient,
  { jobId }: GetHanaViewWriteBackJobPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetHanaViewWriteBackJobQueryKey({ jobId }) }, options);

  return queryClient;
};

/**
 * @summary Import Hana View
 */
export const importHanaView = (
  tLHanaViewImportRequest: BodyType<TLHanaViewImportRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLHanaViewResponse>(
    {
      url: `/hana-views/import`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLHanaViewImportRequest,
      signal,
    },
    options,
  );
};

export const getImportHanaViewQueryKey = (tLHanaViewImportRequest?: BodyType<TLHanaViewImportRequest>) => {
  return ['POST', 'hana-views', 'import', tLHanaViewImportRequest] as const;
};

export const getImportHanaViewQueryOptions = <
  TData = Awaited<ReturnType<typeof importHanaView>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLHanaViewImportRequest: BodyType<TLHanaViewImportRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof importHanaView>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getImportHanaViewQueryKey(tLHanaViewImportRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof importHanaView>>> = ({ signal }) =>
    importHanaView(tLHanaViewImportRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof importHanaView>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ImportHanaViewQueryResult = NonNullable<Awaited<ReturnType<typeof importHanaView>>>;
export type ImportHanaViewQueryError = ErrorType<TLHTTPValidationError>;

export function useImportHanaView<
  TData = Awaited<ReturnType<typeof importHanaView>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLHanaViewImportRequest: BodyType<TLHanaViewImportRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof importHanaView>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof importHanaView>>,
          TError,
          Awaited<ReturnType<typeof importHanaView>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useImportHanaView<
  TData = Awaited<ReturnType<typeof importHanaView>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLHanaViewImportRequest: BodyType<TLHanaViewImportRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof importHanaView>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof importHanaView>>,
          TError,
          Awaited<ReturnType<typeof importHanaView>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useImportHanaView<
  TData = Awaited<ReturnType<typeof importHanaView>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLHanaViewImportRequest: BodyType<TLHanaViewImportRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof importHanaView>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Import Hana View
 */

export function useImportHanaView<
  TData = Awaited<ReturnType<typeof importHanaView>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLHanaViewImportRequest: BodyType<TLHanaViewImportRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof importHanaView>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getImportHanaViewQueryOptions(tLHanaViewImportRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Import Hana View
 */
export const prefetchImportHanaViewQuery = async <
  TData = Awaited<ReturnType<typeof importHanaView>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLHanaViewImportRequest: BodyType<TLHanaViewImportRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof importHanaView>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getImportHanaViewQueryOptions(tLHanaViewImportRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Import Hana View
 */
export const invalidateImportHanaView = async (
  queryClient: QueryClient,
  tLHanaViewImportRequest: BodyType<TLHanaViewImportRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getImportHanaViewQueryKey(tLHanaViewImportRequest) }, options);

  return queryClient;
};

/**
 * @summary Bulk Approve Hana View Reviews
 */
export const bulkApproveHanaViewReviews = (
  tLHanaViewBulkApprovalRequest: BodyType<TLHanaViewBulkApprovalRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLHanaViewBulkApprovalResponse>(
    {
      url: `/hana-views/reviews/bulk-approve`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLHanaViewBulkApprovalRequest,
      signal,
    },
    options,
  );
};

export const getBulkApproveHanaViewReviewsQueryKey = (
  tLHanaViewBulkApprovalRequest?: BodyType<TLHanaViewBulkApprovalRequest>,
) => {
  return ['POST', 'hana-views', 'reviews', 'bulk-approve', tLHanaViewBulkApprovalRequest] as const;
};

export const getBulkApproveHanaViewReviewsQueryOptions = <
  TData = Awaited<ReturnType<typeof bulkApproveHanaViewReviews>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLHanaViewBulkApprovalRequest: BodyType<TLHanaViewBulkApprovalRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkApproveHanaViewReviews>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getBulkApproveHanaViewReviewsQueryKey(tLHanaViewBulkApprovalRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof bulkApproveHanaViewReviews>>> = ({ signal }) =>
    bulkApproveHanaViewReviews(tLHanaViewBulkApprovalRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof bulkApproveHanaViewReviews>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type BulkApproveHanaViewReviewsQueryResult = NonNullable<Awaited<ReturnType<typeof bulkApproveHanaViewReviews>>>;
export type BulkApproveHanaViewReviewsQueryError = ErrorType<TLHTTPValidationError>;

export function useBulkApproveHanaViewReviews<
  TData = Awaited<ReturnType<typeof bulkApproveHanaViewReviews>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLHanaViewBulkApprovalRequest: BodyType<TLHanaViewBulkApprovalRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkApproveHanaViewReviews>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof bulkApproveHanaViewReviews>>,
          TError,
          Awaited<ReturnType<typeof bulkApproveHanaViewReviews>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useBulkApproveHanaViewReviews<
  TData = Awaited<ReturnType<typeof bulkApproveHanaViewReviews>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLHanaViewBulkApprovalRequest: BodyType<TLHanaViewBulkApprovalRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkApproveHanaViewReviews>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof bulkApproveHanaViewReviews>>,
          TError,
          Awaited<ReturnType<typeof bulkApproveHanaViewReviews>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useBulkApproveHanaViewReviews<
  TData = Awaited<ReturnType<typeof bulkApproveHanaViewReviews>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLHanaViewBulkApprovalRequest: BodyType<TLHanaViewBulkApprovalRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkApproveHanaViewReviews>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Bulk Approve Hana View Reviews
 */

export function useBulkApproveHanaViewReviews<
  TData = Awaited<ReturnType<typeof bulkApproveHanaViewReviews>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLHanaViewBulkApprovalRequest: BodyType<TLHanaViewBulkApprovalRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkApproveHanaViewReviews>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getBulkApproveHanaViewReviewsQueryOptions(tLHanaViewBulkApprovalRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Bulk Approve Hana View Reviews
 */
export const prefetchBulkApproveHanaViewReviewsQuery = async <
  TData = Awaited<ReturnType<typeof bulkApproveHanaViewReviews>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLHanaViewBulkApprovalRequest: BodyType<TLHanaViewBulkApprovalRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkApproveHanaViewReviews>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getBulkApproveHanaViewReviewsQueryOptions(tLHanaViewBulkApprovalRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Bulk Approve Hana View Reviews
 */
export const invalidateBulkApproveHanaViewReviews = async (
  queryClient: QueryClient,
  tLHanaViewBulkApprovalRequest: BodyType<TLHanaViewBulkApprovalRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getBulkApproveHanaViewReviewsQueryKey(tLHanaViewBulkApprovalRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Bulk Request Hana View Review
 */
export const bulkRequestHanaViewReview = (
  tLHanaViewBulkReviewRequest: BodyType<TLHanaViewBulkReviewRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLHanaViewBulkReviewResponse>(
    {
      url: `/hana-views/reviews/bulk-request`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLHanaViewBulkReviewRequest,
      signal,
    },
    options,
  );
};

export const getBulkRequestHanaViewReviewQueryKey = (
  tLHanaViewBulkReviewRequest?: BodyType<TLHanaViewBulkReviewRequest>,
) => {
  return ['POST', 'hana-views', 'reviews', 'bulk-request', tLHanaViewBulkReviewRequest] as const;
};

export const getBulkRequestHanaViewReviewQueryOptions = <
  TData = Awaited<ReturnType<typeof bulkRequestHanaViewReview>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLHanaViewBulkReviewRequest: BodyType<TLHanaViewBulkReviewRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkRequestHanaViewReview>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getBulkRequestHanaViewReviewQueryKey(tLHanaViewBulkReviewRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof bulkRequestHanaViewReview>>> = ({ signal }) =>
    bulkRequestHanaViewReview(tLHanaViewBulkReviewRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof bulkRequestHanaViewReview>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type BulkRequestHanaViewReviewQueryResult = NonNullable<Awaited<ReturnType<typeof bulkRequestHanaViewReview>>>;
export type BulkRequestHanaViewReviewQueryError = ErrorType<TLHTTPValidationError>;

export function useBulkRequestHanaViewReview<
  TData = Awaited<ReturnType<typeof bulkRequestHanaViewReview>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLHanaViewBulkReviewRequest: BodyType<TLHanaViewBulkReviewRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkRequestHanaViewReview>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof bulkRequestHanaViewReview>>,
          TError,
          Awaited<ReturnType<typeof bulkRequestHanaViewReview>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useBulkRequestHanaViewReview<
  TData = Awaited<ReturnType<typeof bulkRequestHanaViewReview>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLHanaViewBulkReviewRequest: BodyType<TLHanaViewBulkReviewRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkRequestHanaViewReview>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof bulkRequestHanaViewReview>>,
          TError,
          Awaited<ReturnType<typeof bulkRequestHanaViewReview>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useBulkRequestHanaViewReview<
  TData = Awaited<ReturnType<typeof bulkRequestHanaViewReview>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLHanaViewBulkReviewRequest: BodyType<TLHanaViewBulkReviewRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkRequestHanaViewReview>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Bulk Request Hana View Review
 */

export function useBulkRequestHanaViewReview<
  TData = Awaited<ReturnType<typeof bulkRequestHanaViewReview>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLHanaViewBulkReviewRequest: BodyType<TLHanaViewBulkReviewRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkRequestHanaViewReview>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getBulkRequestHanaViewReviewQueryOptions(tLHanaViewBulkReviewRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Bulk Request Hana View Review
 */
export const prefetchBulkRequestHanaViewReviewQuery = async <
  TData = Awaited<ReturnType<typeof bulkRequestHanaViewReview>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLHanaViewBulkReviewRequest: BodyType<TLHanaViewBulkReviewRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkRequestHanaViewReview>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getBulkRequestHanaViewReviewQueryOptions(tLHanaViewBulkReviewRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Bulk Request Hana View Review
 */
export const invalidateBulkRequestHanaViewReview = async (
  queryClient: QueryClient,
  tLHanaViewBulkReviewRequest: BodyType<TLHanaViewBulkReviewRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getBulkRequestHanaViewReviewQueryKey(tLHanaViewBulkReviewRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Search Sap Views
 */
export const searchSapViews = (
  params: SearchSapViewsParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLSapRepositoryViewResponse[]>(
    { url: `/hana-views/sap-search`, method: 'GET', params, signal },
    options,
  );
};

export const getSearchSapViewsQueryKey = (params?: SearchSapViewsParams) => {
  return ['hana-views', 'sap-search', ...(params ? [params] : [])] as const;
};

export const getSearchSapViewsQueryOptions = <
  TData = Awaited<ReturnType<typeof searchSapViews>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: SearchSapViewsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof searchSapViews>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getSearchSapViewsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof searchSapViews>>> = ({ signal }) =>
    searchSapViews(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof searchSapViews>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type SearchSapViewsQueryResult = NonNullable<Awaited<ReturnType<typeof searchSapViews>>>;
export type SearchSapViewsQueryError = ErrorType<TLHTTPValidationError>;

export function useSearchSapViews<
  TData = Awaited<ReturnType<typeof searchSapViews>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: SearchSapViewsParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof searchSapViews>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof searchSapViews>>,
          TError,
          Awaited<ReturnType<typeof searchSapViews>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useSearchSapViews<
  TData = Awaited<ReturnType<typeof searchSapViews>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: SearchSapViewsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof searchSapViews>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof searchSapViews>>,
          TError,
          Awaited<ReturnType<typeof searchSapViews>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useSearchSapViews<
  TData = Awaited<ReturnType<typeof searchSapViews>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: SearchSapViewsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof searchSapViews>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Search Sap Views
 */

export function useSearchSapViews<
  TData = Awaited<ReturnType<typeof searchSapViews>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: SearchSapViewsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof searchSapViews>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getSearchSapViewsQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Search Sap Views
 */
export const prefetchSearchSapViewsQuery = async <
  TData = Awaited<ReturnType<typeof searchSapViews>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  params: SearchSapViewsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof searchSapViews>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getSearchSapViewsQueryOptions(params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Search Sap Views
 */
export const invalidateSearchSapViews = async (
  queryClient: QueryClient,
  params: SearchSapViewsParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getSearchSapViewsQueryKey(params) }, options);

  return queryClient;
};

/**
 * @summary Setup Hana Views
 */
export const setupHanaViews = (
  tLHanaViewSetupRequest: BodyType<TLHanaViewSetupRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLHanaViewSetupResponse>(
    {
      url: `/hana-views/setup`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLHanaViewSetupRequest,
      signal,
    },
    options,
  );
};

export const getSetupHanaViewsQueryKey = (tLHanaViewSetupRequest?: BodyType<TLHanaViewSetupRequest>) => {
  return ['POST', 'hana-views', 'setup', tLHanaViewSetupRequest] as const;
};

export const getSetupHanaViewsQueryOptions = <
  TData = Awaited<ReturnType<typeof setupHanaViews>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLHanaViewSetupRequest: BodyType<TLHanaViewSetupRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof setupHanaViews>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getSetupHanaViewsQueryKey(tLHanaViewSetupRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof setupHanaViews>>> = ({ signal }) =>
    setupHanaViews(tLHanaViewSetupRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof setupHanaViews>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type SetupHanaViewsQueryResult = NonNullable<Awaited<ReturnType<typeof setupHanaViews>>>;
export type SetupHanaViewsQueryError = ErrorType<TLHTTPValidationError>;

export function useSetupHanaViews<
  TData = Awaited<ReturnType<typeof setupHanaViews>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLHanaViewSetupRequest: BodyType<TLHanaViewSetupRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof setupHanaViews>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof setupHanaViews>>,
          TError,
          Awaited<ReturnType<typeof setupHanaViews>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useSetupHanaViews<
  TData = Awaited<ReturnType<typeof setupHanaViews>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLHanaViewSetupRequest: BodyType<TLHanaViewSetupRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof setupHanaViews>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof setupHanaViews>>,
          TError,
          Awaited<ReturnType<typeof setupHanaViews>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useSetupHanaViews<
  TData = Awaited<ReturnType<typeof setupHanaViews>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLHanaViewSetupRequest: BodyType<TLHanaViewSetupRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof setupHanaViews>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Setup Hana Views
 */

export function useSetupHanaViews<
  TData = Awaited<ReturnType<typeof setupHanaViews>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLHanaViewSetupRequest: BodyType<TLHanaViewSetupRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof setupHanaViews>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getSetupHanaViewsQueryOptions(tLHanaViewSetupRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Setup Hana Views
 */
export const prefetchSetupHanaViewsQuery = async <
  TData = Awaited<ReturnType<typeof setupHanaViews>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLHanaViewSetupRequest: BodyType<TLHanaViewSetupRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof setupHanaViews>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getSetupHanaViewsQueryOptions(tLHanaViewSetupRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Setup Hana Views
 */
export const invalidateSetupHanaViews = async (
  queryClient: QueryClient,
  tLHanaViewSetupRequest: BodyType<TLHanaViewSetupRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getSetupHanaViewsQueryKey(tLHanaViewSetupRequest) }, options);

  return queryClient;
};

/**
 * @summary Get Setup Job Status
 */
export const getSetupJobStatus = (
  { jobId }: GetSetupJobStatusPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLHanaViewSetupJobStatusResponse>(
    { url: `/hana-views/setup/${jobId}`, method: 'GET', signal },
    options,
  );
};

export const getGetSetupJobStatusQueryKey = ({ jobId }: GetSetupJobStatusPathParameters) => {
  return ['hana-views', 'setup', jobId] as const;
};

export const getGetSetupJobStatusQueryOptions = <
  TData = Awaited<ReturnType<typeof getSetupJobStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { jobId }: GetSetupJobStatusPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSetupJobStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetSetupJobStatusQueryKey({ jobId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getSetupJobStatus>>> = ({ signal }) =>
    getSetupJobStatus({ jobId }, requestOptions, signal);

  return { queryKey, queryFn, enabled: jobId !== null && jobId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getSetupJobStatus>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetSetupJobStatusQueryResult = NonNullable<Awaited<ReturnType<typeof getSetupJobStatus>>>;
export type GetSetupJobStatusQueryError = ErrorType<TLHTTPValidationError>;

export function useGetSetupJobStatus<
  TData = Awaited<ReturnType<typeof getSetupJobStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetSetupJobStatusPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSetupJobStatus>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getSetupJobStatus>>,
          TError,
          Awaited<ReturnType<typeof getSetupJobStatus>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetSetupJobStatus<
  TData = Awaited<ReturnType<typeof getSetupJobStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetSetupJobStatusPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSetupJobStatus>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getSetupJobStatus>>,
          TError,
          Awaited<ReturnType<typeof getSetupJobStatus>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetSetupJobStatus<
  TData = Awaited<ReturnType<typeof getSetupJobStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetSetupJobStatusPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSetupJobStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Setup Job Status
 */

export function useGetSetupJobStatus<
  TData = Awaited<ReturnType<typeof getSetupJobStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { jobId }: GetSetupJobStatusPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSetupJobStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetSetupJobStatusQueryOptions({ jobId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Setup Job Status
 */
export const prefetchGetSetupJobStatusQuery = async <
  TData = Awaited<ReturnType<typeof getSetupJobStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { jobId }: GetSetupJobStatusPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSetupJobStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetSetupJobStatusQueryOptions({ jobId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Setup Job Status
 */
export const invalidateGetSetupJobStatus = async (
  queryClient: QueryClient,
  { jobId }: GetSetupJobStatusPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetSetupJobStatusQueryKey({ jobId }) }, options);

  return queryClient;
};

/**
 * @summary Upload Hana View
 */
export const uploadHanaView = (
  tLHanaViewUploadRequest: BodyType<TLHanaViewUploadRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLHanaViewResponse>(
    {
      url: `/hana-views/upload`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLHanaViewUploadRequest,
      signal,
    },
    options,
  );
};

export const getUploadHanaViewQueryKey = (tLHanaViewUploadRequest?: BodyType<TLHanaViewUploadRequest>) => {
  return ['POST', 'hana-views', 'upload', tLHanaViewUploadRequest] as const;
};

export const getUploadHanaViewQueryOptions = <
  TData = Awaited<ReturnType<typeof uploadHanaView>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLHanaViewUploadRequest: BodyType<TLHanaViewUploadRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof uploadHanaView>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getUploadHanaViewQueryKey(tLHanaViewUploadRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof uploadHanaView>>> = ({ signal }) =>
    uploadHanaView(tLHanaViewUploadRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof uploadHanaView>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type UploadHanaViewQueryResult = NonNullable<Awaited<ReturnType<typeof uploadHanaView>>>;
export type UploadHanaViewQueryError = ErrorType<TLHTTPValidationError>;

export function useUploadHanaView<
  TData = Awaited<ReturnType<typeof uploadHanaView>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLHanaViewUploadRequest: BodyType<TLHanaViewUploadRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof uploadHanaView>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof uploadHanaView>>,
          TError,
          Awaited<ReturnType<typeof uploadHanaView>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUploadHanaView<
  TData = Awaited<ReturnType<typeof uploadHanaView>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLHanaViewUploadRequest: BodyType<TLHanaViewUploadRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof uploadHanaView>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof uploadHanaView>>,
          TError,
          Awaited<ReturnType<typeof uploadHanaView>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUploadHanaView<
  TData = Awaited<ReturnType<typeof uploadHanaView>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLHanaViewUploadRequest: BodyType<TLHanaViewUploadRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof uploadHanaView>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Upload Hana View
 */

export function useUploadHanaView<
  TData = Awaited<ReturnType<typeof uploadHanaView>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLHanaViewUploadRequest: BodyType<TLHanaViewUploadRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof uploadHanaView>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getUploadHanaViewQueryOptions(tLHanaViewUploadRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Upload Hana View
 */
export const prefetchUploadHanaViewQuery = async <
  TData = Awaited<ReturnType<typeof uploadHanaView>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLHanaViewUploadRequest: BodyType<TLHanaViewUploadRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof uploadHanaView>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getUploadHanaViewQueryOptions(tLHanaViewUploadRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Upload Hana View
 */
export const invalidateUploadHanaView = async (
  queryClient: QueryClient,
  tLHanaViewUploadRequest: BodyType<TLHanaViewUploadRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getUploadHanaViewQueryKey(tLHanaViewUploadRequest) }, options);

  return queryClient;
};

/**
 * @summary Get Hana View
 */
export const getHanaView = (
  { viewId }: GetHanaViewPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLHanaViewResponse>({ url: `/hana-views/${viewId}`, method: 'GET', signal }, options);
};

export const getGetHanaViewQueryKey = ({ viewId }: GetHanaViewPathParameters) => {
  return ['hana-views', viewId] as const;
};

export const getGetHanaViewQueryOptions = <
  TData = Awaited<ReturnType<typeof getHanaView>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { viewId }: GetHanaViewPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getHanaView>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetHanaViewQueryKey({ viewId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getHanaView>>> = ({ signal }) =>
    getHanaView({ viewId }, requestOptions, signal);

  return { queryKey, queryFn, enabled: viewId !== null && viewId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getHanaView>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetHanaViewQueryResult = NonNullable<Awaited<ReturnType<typeof getHanaView>>>;
export type GetHanaViewQueryError = ErrorType<TLHTTPValidationError>;

export function useGetHanaView<
  TData = Awaited<ReturnType<typeof getHanaView>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetHanaViewPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getHanaView>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getHanaView>>,
          TError,
          Awaited<ReturnType<typeof getHanaView>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetHanaView<
  TData = Awaited<ReturnType<typeof getHanaView>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetHanaViewPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getHanaView>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getHanaView>>,
          TError,
          Awaited<ReturnType<typeof getHanaView>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetHanaView<
  TData = Awaited<ReturnType<typeof getHanaView>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetHanaViewPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getHanaView>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Hana View
 */

export function useGetHanaView<
  TData = Awaited<ReturnType<typeof getHanaView>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { viewId }: GetHanaViewPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getHanaView>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetHanaViewQueryOptions({ viewId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Hana View
 */
export const prefetchGetHanaViewQuery = async <
  TData = Awaited<ReturnType<typeof getHanaView>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { viewId }: GetHanaViewPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getHanaView>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetHanaViewQueryOptions({ viewId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Hana View
 */
export const invalidateGetHanaView = async (
  queryClient: QueryClient,
  { viewId }: GetHanaViewPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetHanaViewQueryKey({ viewId }) }, options);

  return queryClient;
};

/**
 * @summary Update Hana View Metadata
 */
export const updateHanaViewMetadata = (
  { viewId }: UpdateHanaViewMetadataPathParameters,
  tLHanaViewUpdateRequest: BodyType<TLHanaViewUpdateRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLHanaViewResponse>(
    {
      url: `/hana-views/${viewId}`,
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      data: tLHanaViewUpdateRequest,
      signal,
    },
    options,
  );
};

export const getUpdateHanaViewMetadataQueryKey = (
  { viewId }: UpdateHanaViewMetadataPathParameters,
  tLHanaViewUpdateRequest?: BodyType<TLHanaViewUpdateRequest>,
) => {
  return ['PATCH', 'hana-views', viewId, tLHanaViewUpdateRequest] as const;
};

export const getUpdateHanaViewMetadataQueryOptions = <
  TData = Awaited<ReturnType<typeof updateHanaViewMetadata>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { viewId }: UpdateHanaViewMetadataPathParameters,
  tLHanaViewUpdateRequest: BodyType<TLHanaViewUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateHanaViewMetadata>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getUpdateHanaViewMetadataQueryKey({ viewId }, tLHanaViewUpdateRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof updateHanaViewMetadata>>> = ({ signal }) =>
    updateHanaViewMetadata({ viewId }, tLHanaViewUpdateRequest, requestOptions, signal);

  return { queryKey, queryFn, enabled: viewId !== null && viewId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof updateHanaViewMetadata>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type UpdateHanaViewMetadataQueryResult = NonNullable<Awaited<ReturnType<typeof updateHanaViewMetadata>>>;
export type UpdateHanaViewMetadataQueryError = ErrorType<TLHTTPValidationError>;

export function useUpdateHanaViewMetadata<
  TData = Awaited<ReturnType<typeof updateHanaViewMetadata>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateHanaViewMetadataPathParameters,
  tLHanaViewUpdateRequest: BodyType<TLHanaViewUpdateRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateHanaViewMetadata>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateHanaViewMetadata>>,
          TError,
          Awaited<ReturnType<typeof updateHanaViewMetadata>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateHanaViewMetadata<
  TData = Awaited<ReturnType<typeof updateHanaViewMetadata>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateHanaViewMetadataPathParameters,
  tLHanaViewUpdateRequest: BodyType<TLHanaViewUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateHanaViewMetadata>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateHanaViewMetadata>>,
          TError,
          Awaited<ReturnType<typeof updateHanaViewMetadata>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateHanaViewMetadata<
  TData = Awaited<ReturnType<typeof updateHanaViewMetadata>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateHanaViewMetadataPathParameters,
  tLHanaViewUpdateRequest: BodyType<TLHanaViewUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateHanaViewMetadata>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Update Hana View Metadata
 */

export function useUpdateHanaViewMetadata<
  TData = Awaited<ReturnType<typeof updateHanaViewMetadata>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { viewId }: UpdateHanaViewMetadataPathParameters,
  tLHanaViewUpdateRequest: BodyType<TLHanaViewUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateHanaViewMetadata>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getUpdateHanaViewMetadataQueryOptions({ viewId }, tLHanaViewUpdateRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Update Hana View Metadata
 */
export const prefetchUpdateHanaViewMetadataQuery = async <
  TData = Awaited<ReturnType<typeof updateHanaViewMetadata>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { viewId }: UpdateHanaViewMetadataPathParameters,
  tLHanaViewUpdateRequest: BodyType<TLHanaViewUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateHanaViewMetadata>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getUpdateHanaViewMetadataQueryOptions({ viewId }, tLHanaViewUpdateRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Update Hana View Metadata
 */
export const invalidateUpdateHanaViewMetadata = async (
  queryClient: QueryClient,
  { viewId }: UpdateHanaViewMetadataPathParameters,
  tLHanaViewUpdateRequest: BodyType<TLHanaViewUpdateRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getUpdateHanaViewMetadataQueryKey({ viewId }, tLHanaViewUpdateRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary List Hana View Activity
 */
export const listHanaViewActivity = (
  { viewId }: ListHanaViewActivityPathParameters,
  params?: ListHanaViewActivityParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLViewActivityResponse>(
    { url: `/hana-views/${viewId}/activity`, method: 'GET', params, signal },
    options,
  );
};

export const getListHanaViewActivityQueryKey = (
  { viewId }: ListHanaViewActivityPathParameters,
  params?: ListHanaViewActivityParams,
) => {
  return ['hana-views', viewId, 'activity', ...(params ? [params] : [])] as const;
};

export const getListHanaViewActivityQueryOptions = <
  TData = Awaited<ReturnType<typeof listHanaViewActivity>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { viewId }: ListHanaViewActivityPathParameters,
  params?: ListHanaViewActivityParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listHanaViewActivity>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListHanaViewActivityQueryKey({ viewId }, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listHanaViewActivity>>> = ({ signal }) =>
    listHanaViewActivity({ viewId }, params, requestOptions, signal);

  return { queryKey, queryFn, enabled: viewId !== null && viewId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listHanaViewActivity>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListHanaViewActivityQueryResult = NonNullable<Awaited<ReturnType<typeof listHanaViewActivity>>>;
export type ListHanaViewActivityQueryError = ErrorType<TLHTTPValidationError>;

export function useListHanaViewActivity<
  TData = Awaited<ReturnType<typeof listHanaViewActivity>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ListHanaViewActivityPathParameters,
  params: undefined | ListHanaViewActivityParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof listHanaViewActivity>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listHanaViewActivity>>,
          TError,
          Awaited<ReturnType<typeof listHanaViewActivity>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListHanaViewActivity<
  TData = Awaited<ReturnType<typeof listHanaViewActivity>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ListHanaViewActivityPathParameters,
  params?: ListHanaViewActivityParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listHanaViewActivity>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listHanaViewActivity>>,
          TError,
          Awaited<ReturnType<typeof listHanaViewActivity>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListHanaViewActivity<
  TData = Awaited<ReturnType<typeof listHanaViewActivity>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ListHanaViewActivityPathParameters,
  params?: ListHanaViewActivityParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listHanaViewActivity>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List Hana View Activity
 */

export function useListHanaViewActivity<
  TData = Awaited<ReturnType<typeof listHanaViewActivity>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { viewId }: ListHanaViewActivityPathParameters,
  params?: ListHanaViewActivityParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listHanaViewActivity>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListHanaViewActivityQueryOptions({ viewId }, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary List Hana View Activity
 */
export const prefetchListHanaViewActivityQuery = async <
  TData = Awaited<ReturnType<typeof listHanaViewActivity>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { viewId }: ListHanaViewActivityPathParameters,
  params?: ListHanaViewActivityParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listHanaViewActivity>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getListHanaViewActivityQueryOptions({ viewId }, params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary List Hana View Activity
 */
export const invalidateListHanaViewActivity = async (
  queryClient: QueryClient,
  { viewId }: ListHanaViewActivityPathParameters,
  params?: ListHanaViewActivityParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getListHanaViewActivityQueryKey({ viewId }, params) }, options);

  return queryClient;
};

/**
 * @summary Assign User To Hana View
 */
export const assignUserToHanaView = (
  { viewId }: AssignUserToHanaViewPathParameters,
  tLHanaViewAssignmentRequest: BodyType<TLHanaViewAssignmentRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLHanaViewResponse>(
    {
      url: `/hana-views/${viewId}/assign`,
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      data: tLHanaViewAssignmentRequest,
      signal,
    },
    options,
  );
};

export const getAssignUserToHanaViewQueryKey = (
  { viewId }: AssignUserToHanaViewPathParameters,
  tLHanaViewAssignmentRequest?: BodyType<TLHanaViewAssignmentRequest>,
) => {
  return ['PATCH', 'hana-views', viewId, 'assign', tLHanaViewAssignmentRequest] as const;
};

export const getAssignUserToHanaViewQueryOptions = <
  TData = Awaited<ReturnType<typeof assignUserToHanaView>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { viewId }: AssignUserToHanaViewPathParameters,
  tLHanaViewAssignmentRequest: BodyType<TLHanaViewAssignmentRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof assignUserToHanaView>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getAssignUserToHanaViewQueryKey({ viewId }, tLHanaViewAssignmentRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof assignUserToHanaView>>> = ({ signal }) =>
    assignUserToHanaView({ viewId }, tLHanaViewAssignmentRequest, requestOptions, signal);

  return { queryKey, queryFn, enabled: viewId !== null && viewId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof assignUserToHanaView>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type AssignUserToHanaViewQueryResult = NonNullable<Awaited<ReturnType<typeof assignUserToHanaView>>>;
export type AssignUserToHanaViewQueryError = ErrorType<TLHTTPValidationError>;

export function useAssignUserToHanaView<
  TData = Awaited<ReturnType<typeof assignUserToHanaView>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: AssignUserToHanaViewPathParameters,
  tLHanaViewAssignmentRequest: BodyType<TLHanaViewAssignmentRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof assignUserToHanaView>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof assignUserToHanaView>>,
          TError,
          Awaited<ReturnType<typeof assignUserToHanaView>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useAssignUserToHanaView<
  TData = Awaited<ReturnType<typeof assignUserToHanaView>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: AssignUserToHanaViewPathParameters,
  tLHanaViewAssignmentRequest: BodyType<TLHanaViewAssignmentRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof assignUserToHanaView>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof assignUserToHanaView>>,
          TError,
          Awaited<ReturnType<typeof assignUserToHanaView>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useAssignUserToHanaView<
  TData = Awaited<ReturnType<typeof assignUserToHanaView>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: AssignUserToHanaViewPathParameters,
  tLHanaViewAssignmentRequest: BodyType<TLHanaViewAssignmentRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof assignUserToHanaView>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Assign User To Hana View
 */

export function useAssignUserToHanaView<
  TData = Awaited<ReturnType<typeof assignUserToHanaView>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { viewId }: AssignUserToHanaViewPathParameters,
  tLHanaViewAssignmentRequest: BodyType<TLHanaViewAssignmentRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof assignUserToHanaView>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getAssignUserToHanaViewQueryOptions({ viewId }, tLHanaViewAssignmentRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Assign User To Hana View
 */
export const prefetchAssignUserToHanaViewQuery = async <
  TData = Awaited<ReturnType<typeof assignUserToHanaView>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { viewId }: AssignUserToHanaViewPathParameters,
  tLHanaViewAssignmentRequest: BodyType<TLHanaViewAssignmentRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof assignUserToHanaView>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getAssignUserToHanaViewQueryOptions({ viewId }, tLHanaViewAssignmentRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Assign User To Hana View
 */
export const invalidateAssignUserToHanaView = async (
  queryClient: QueryClient,
  { viewId }: AssignUserToHanaViewPathParameters,
  tLHanaViewAssignmentRequest: BodyType<TLHanaViewAssignmentRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getAssignUserToHanaViewQueryKey({ viewId }, tLHanaViewAssignmentRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Get Hana View Dag
 */
export const getHanaViewDag = (
  { viewId }: GetHanaViewDagPathParameters,
  params?: GetHanaViewDagParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLCalculationViewDag>(
    { url: `/hana-views/${viewId}/dag`, method: 'GET', params, signal },
    options,
  );
};

export const getGetHanaViewDagQueryKey = ({ viewId }: GetHanaViewDagPathParameters, params?: GetHanaViewDagParams) => {
  return ['hana-views', viewId, 'dag', ...(params ? [params] : [])] as const;
};

export const getGetHanaViewDagQueryOptions = <
  TData = Awaited<ReturnType<typeof getHanaViewDag>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { viewId }: GetHanaViewDagPathParameters,
  params?: GetHanaViewDagParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getHanaViewDag>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetHanaViewDagQueryKey({ viewId }, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getHanaViewDag>>> = ({ signal }) =>
    getHanaViewDag({ viewId }, params, requestOptions, signal);

  return { queryKey, queryFn, enabled: viewId !== null && viewId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getHanaViewDag>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetHanaViewDagQueryResult = NonNullable<Awaited<ReturnType<typeof getHanaViewDag>>>;
export type GetHanaViewDagQueryError = ErrorType<TLHTTPValidationError>;

export function useGetHanaViewDag<
  TData = Awaited<ReturnType<typeof getHanaViewDag>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetHanaViewDagPathParameters,
  params: undefined | GetHanaViewDagParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getHanaViewDag>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getHanaViewDag>>,
          TError,
          Awaited<ReturnType<typeof getHanaViewDag>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetHanaViewDag<
  TData = Awaited<ReturnType<typeof getHanaViewDag>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetHanaViewDagPathParameters,
  params?: GetHanaViewDagParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getHanaViewDag>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getHanaViewDag>>,
          TError,
          Awaited<ReturnType<typeof getHanaViewDag>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetHanaViewDag<
  TData = Awaited<ReturnType<typeof getHanaViewDag>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetHanaViewDagPathParameters,
  params?: GetHanaViewDagParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getHanaViewDag>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Hana View Dag
 */

export function useGetHanaViewDag<
  TData = Awaited<ReturnType<typeof getHanaViewDag>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { viewId }: GetHanaViewDagPathParameters,
  params?: GetHanaViewDagParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getHanaViewDag>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetHanaViewDagQueryOptions({ viewId }, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Hana View Dag
 */
export const prefetchGetHanaViewDagQuery = async <
  TData = Awaited<ReturnType<typeof getHanaViewDag>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { viewId }: GetHanaViewDagPathParameters,
  params?: GetHanaViewDagParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getHanaViewDag>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetHanaViewDagQueryOptions({ viewId }, params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Hana View Dag
 */
export const invalidateGetHanaViewDag = async (
  queryClient: QueryClient,
  { viewId }: GetHanaViewDagPathParameters,
  params?: GetHanaViewDagParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetHanaViewDagQueryKey({ viewId }, params) }, options);

  return queryClient;
};

/**
 * @summary Get Hana View Procedure Sql
 */
export const getHanaViewProcedureSql = (
  { viewId }: GetHanaViewProcedureSqlPathParameters,
  params?: GetHanaViewProcedureSqlParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLProcedureSqlResponse>(
    { url: `/hana-views/${viewId}/procedure-sql`, method: 'GET', params, signal },
    options,
  );
};

export const getGetHanaViewProcedureSqlQueryKey = (
  { viewId }: GetHanaViewProcedureSqlPathParameters,
  params?: GetHanaViewProcedureSqlParams,
) => {
  return ['hana-views', viewId, 'procedure-sql', ...(params ? [params] : [])] as const;
};

export const getGetHanaViewProcedureSqlQueryOptions = <
  TData = Awaited<ReturnType<typeof getHanaViewProcedureSql>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { viewId }: GetHanaViewProcedureSqlPathParameters,
  params?: GetHanaViewProcedureSqlParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getHanaViewProcedureSql>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetHanaViewProcedureSqlQueryKey({ viewId }, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getHanaViewProcedureSql>>> = ({ signal }) =>
    getHanaViewProcedureSql({ viewId }, params, requestOptions, signal);

  return { queryKey, queryFn, enabled: viewId !== null && viewId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getHanaViewProcedureSql>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetHanaViewProcedureSqlQueryResult = NonNullable<Awaited<ReturnType<typeof getHanaViewProcedureSql>>>;
export type GetHanaViewProcedureSqlQueryError = ErrorType<TLHTTPValidationError>;

export function useGetHanaViewProcedureSql<
  TData = Awaited<ReturnType<typeof getHanaViewProcedureSql>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetHanaViewProcedureSqlPathParameters,
  params: undefined | GetHanaViewProcedureSqlParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getHanaViewProcedureSql>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getHanaViewProcedureSql>>,
          TError,
          Awaited<ReturnType<typeof getHanaViewProcedureSql>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetHanaViewProcedureSql<
  TData = Awaited<ReturnType<typeof getHanaViewProcedureSql>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetHanaViewProcedureSqlPathParameters,
  params?: GetHanaViewProcedureSqlParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getHanaViewProcedureSql>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getHanaViewProcedureSql>>,
          TError,
          Awaited<ReturnType<typeof getHanaViewProcedureSql>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetHanaViewProcedureSql<
  TData = Awaited<ReturnType<typeof getHanaViewProcedureSql>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetHanaViewProcedureSqlPathParameters,
  params?: GetHanaViewProcedureSqlParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getHanaViewProcedureSql>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Hana View Procedure Sql
 */

export function useGetHanaViewProcedureSql<
  TData = Awaited<ReturnType<typeof getHanaViewProcedureSql>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { viewId }: GetHanaViewProcedureSqlPathParameters,
  params?: GetHanaViewProcedureSqlParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getHanaViewProcedureSql>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetHanaViewProcedureSqlQueryOptions({ viewId }, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Hana View Procedure Sql
 */
export const prefetchGetHanaViewProcedureSqlQuery = async <
  TData = Awaited<ReturnType<typeof getHanaViewProcedureSql>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { viewId }: GetHanaViewProcedureSqlPathParameters,
  params?: GetHanaViewProcedureSqlParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getHanaViewProcedureSql>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetHanaViewProcedureSqlQueryOptions({ viewId }, params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Hana View Procedure Sql
 */
export const invalidateGetHanaViewProcedureSql = async (
  queryClient: QueryClient,
  { viewId }: GetHanaViewProcedureSqlPathParameters,
  params?: GetHanaViewProcedureSqlParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetHanaViewProcedureSqlQueryKey({ viewId }, params) }, options);

  return queryClient;
};

/**
 * @summary Publish Hana View To Sap
 */
export const publishHanaViewToSap = (
  { viewId }: PublishHanaViewToSapPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLHanaViewResponse>({ url: `/hana-views/${viewId}/publish`, method: 'POST', signal }, options);
};

export const getPublishHanaViewToSapQueryKey = ({ viewId }: PublishHanaViewToSapPathParameters) => {
  return ['POST', 'hana-views', viewId, 'publish'] as const;
};

export const getPublishHanaViewToSapQueryOptions = <
  TData = Awaited<ReturnType<typeof publishHanaViewToSap>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { viewId }: PublishHanaViewToSapPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof publishHanaViewToSap>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getPublishHanaViewToSapQueryKey({ viewId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof publishHanaViewToSap>>> = ({ signal }) =>
    publishHanaViewToSap({ viewId }, requestOptions, signal);

  return { queryKey, queryFn, enabled: viewId !== null && viewId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof publishHanaViewToSap>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type PublishHanaViewToSapQueryResult = NonNullable<Awaited<ReturnType<typeof publishHanaViewToSap>>>;
export type PublishHanaViewToSapQueryError = ErrorType<TLHTTPValidationError>;

export function usePublishHanaViewToSap<
  TData = Awaited<ReturnType<typeof publishHanaViewToSap>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: PublishHanaViewToSapPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof publishHanaViewToSap>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof publishHanaViewToSap>>,
          TError,
          Awaited<ReturnType<typeof publishHanaViewToSap>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePublishHanaViewToSap<
  TData = Awaited<ReturnType<typeof publishHanaViewToSap>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: PublishHanaViewToSapPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof publishHanaViewToSap>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof publishHanaViewToSap>>,
          TError,
          Awaited<ReturnType<typeof publishHanaViewToSap>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePublishHanaViewToSap<
  TData = Awaited<ReturnType<typeof publishHanaViewToSap>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: PublishHanaViewToSapPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof publishHanaViewToSap>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Publish Hana View To Sap
 */

export function usePublishHanaViewToSap<
  TData = Awaited<ReturnType<typeof publishHanaViewToSap>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { viewId }: PublishHanaViewToSapPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof publishHanaViewToSap>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getPublishHanaViewToSapQueryOptions({ viewId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Publish Hana View To Sap
 */
export const prefetchPublishHanaViewToSapQuery = async <
  TData = Awaited<ReturnType<typeof publishHanaViewToSap>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { viewId }: PublishHanaViewToSapPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof publishHanaViewToSap>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getPublishHanaViewToSapQueryOptions({ viewId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Publish Hana View To Sap
 */
export const invalidatePublishHanaViewToSap = async (
  queryClient: QueryClient,
  { viewId }: PublishHanaViewToSapPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getPublishHanaViewToSapQueryKey({ viewId }) }, options);

  return queryClient;
};

/**
 * @summary Upload Remediated Hana View Xml
 */
export const uploadRemediatedHanaViewXml = (
  { viewId }: UploadRemediatedHanaViewXmlPathParameters,
  tLUploadRemediatedHanaViewXmlRequest: BodyType<TLUploadRemediatedHanaViewXmlRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  const formData = new FormData();
  formData.append(`file`, tLUploadRemediatedHanaViewXmlRequest.file);

  return apiInstance<TLHanaViewResponse>(
    {
      url: `/hana-views/${viewId}/remediated-xml`,
      method: 'POST',
      headers: { 'Content-Type': 'multipart/form-data' },
      data: formData,
      signal,
    },
    options,
  );
};

export const getUploadRemediatedHanaViewXmlQueryKey = (
  { viewId }: UploadRemediatedHanaViewXmlPathParameters,
  tLUploadRemediatedHanaViewXmlRequest?: BodyType<TLUploadRemediatedHanaViewXmlRequest>,
) => {
  return ['POST', 'hana-views', viewId, 'remediated-xml', tLUploadRemediatedHanaViewXmlRequest] as const;
};

export const getUploadRemediatedHanaViewXmlQueryOptions = <
  TData = Awaited<ReturnType<typeof uploadRemediatedHanaViewXml>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { viewId }: UploadRemediatedHanaViewXmlPathParameters,
  tLUploadRemediatedHanaViewXmlRequest: BodyType<TLUploadRemediatedHanaViewXmlRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof uploadRemediatedHanaViewXml>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getUploadRemediatedHanaViewXmlQueryKey({ viewId }, tLUploadRemediatedHanaViewXmlRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof uploadRemediatedHanaViewXml>>> = ({ signal }) =>
    uploadRemediatedHanaViewXml({ viewId }, tLUploadRemediatedHanaViewXmlRequest, requestOptions, signal);

  return { queryKey, queryFn, enabled: viewId !== null && viewId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof uploadRemediatedHanaViewXml>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type UploadRemediatedHanaViewXmlQueryResult = NonNullable<
  Awaited<ReturnType<typeof uploadRemediatedHanaViewXml>>
>;
export type UploadRemediatedHanaViewXmlQueryError = ErrorType<TLHTTPValidationError>;

export function useUploadRemediatedHanaViewXml<
  TData = Awaited<ReturnType<typeof uploadRemediatedHanaViewXml>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UploadRemediatedHanaViewXmlPathParameters,
  tLUploadRemediatedHanaViewXmlRequest: BodyType<TLUploadRemediatedHanaViewXmlRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof uploadRemediatedHanaViewXml>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof uploadRemediatedHanaViewXml>>,
          TError,
          Awaited<ReturnType<typeof uploadRemediatedHanaViewXml>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUploadRemediatedHanaViewXml<
  TData = Awaited<ReturnType<typeof uploadRemediatedHanaViewXml>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UploadRemediatedHanaViewXmlPathParameters,
  tLUploadRemediatedHanaViewXmlRequest: BodyType<TLUploadRemediatedHanaViewXmlRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof uploadRemediatedHanaViewXml>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof uploadRemediatedHanaViewXml>>,
          TError,
          Awaited<ReturnType<typeof uploadRemediatedHanaViewXml>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUploadRemediatedHanaViewXml<
  TData = Awaited<ReturnType<typeof uploadRemediatedHanaViewXml>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UploadRemediatedHanaViewXmlPathParameters,
  tLUploadRemediatedHanaViewXmlRequest: BodyType<TLUploadRemediatedHanaViewXmlRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof uploadRemediatedHanaViewXml>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Upload Remediated Hana View Xml
 */

export function useUploadRemediatedHanaViewXml<
  TData = Awaited<ReturnType<typeof uploadRemediatedHanaViewXml>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { viewId }: UploadRemediatedHanaViewXmlPathParameters,
  tLUploadRemediatedHanaViewXmlRequest: BodyType<TLUploadRemediatedHanaViewXmlRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof uploadRemediatedHanaViewXml>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getUploadRemediatedHanaViewXmlQueryOptions(
    { viewId },
    tLUploadRemediatedHanaViewXmlRequest,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Upload Remediated Hana View Xml
 */
export const prefetchUploadRemediatedHanaViewXmlQuery = async <
  TData = Awaited<ReturnType<typeof uploadRemediatedHanaViewXml>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { viewId }: UploadRemediatedHanaViewXmlPathParameters,
  tLUploadRemediatedHanaViewXmlRequest: BodyType<TLUploadRemediatedHanaViewXmlRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof uploadRemediatedHanaViewXml>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getUploadRemediatedHanaViewXmlQueryOptions(
    { viewId },
    tLUploadRemediatedHanaViewXmlRequest,
    options,
  );

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Upload Remediated Hana View Xml
 */
export const invalidateUploadRemediatedHanaViewXml = async (
  queryClient: QueryClient,
  { viewId }: UploadRemediatedHanaViewXmlPathParameters,
  tLUploadRemediatedHanaViewXmlRequest: BodyType<TLUploadRemediatedHanaViewXmlRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getUploadRemediatedHanaViewXmlQueryKey({ viewId }, tLUploadRemediatedHanaViewXmlRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Submit Hana View For Review
 */
export const submitHanaViewForReview = (
  { viewId }: SubmitHanaViewForReviewPathParameters,
  tLHanaViewReviewerAssignmentRequest: BodyType<TLHanaViewReviewerAssignmentRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLHanaViewResponse>(
    {
      url: `/hana-views/${viewId}/reviews`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLHanaViewReviewerAssignmentRequest,
      signal,
    },
    options,
  );
};

export const getSubmitHanaViewForReviewQueryKey = (
  { viewId }: SubmitHanaViewForReviewPathParameters,
  tLHanaViewReviewerAssignmentRequest?: BodyType<TLHanaViewReviewerAssignmentRequest>,
) => {
  return ['POST', 'hana-views', viewId, 'reviews', tLHanaViewReviewerAssignmentRequest] as const;
};

export const getSubmitHanaViewForReviewQueryOptions = <
  TData = Awaited<ReturnType<typeof submitHanaViewForReview>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { viewId }: SubmitHanaViewForReviewPathParameters,
  tLHanaViewReviewerAssignmentRequest: BodyType<TLHanaViewReviewerAssignmentRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof submitHanaViewForReview>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getSubmitHanaViewForReviewQueryKey({ viewId }, tLHanaViewReviewerAssignmentRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof submitHanaViewForReview>>> = ({ signal }) =>
    submitHanaViewForReview({ viewId }, tLHanaViewReviewerAssignmentRequest, requestOptions, signal);

  return { queryKey, queryFn, enabled: viewId !== null && viewId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof submitHanaViewForReview>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type SubmitHanaViewForReviewQueryResult = NonNullable<Awaited<ReturnType<typeof submitHanaViewForReview>>>;
export type SubmitHanaViewForReviewQueryError = ErrorType<TLHTTPValidationError>;

export function useSubmitHanaViewForReview<
  TData = Awaited<ReturnType<typeof submitHanaViewForReview>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: SubmitHanaViewForReviewPathParameters,
  tLHanaViewReviewerAssignmentRequest: BodyType<TLHanaViewReviewerAssignmentRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof submitHanaViewForReview>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof submitHanaViewForReview>>,
          TError,
          Awaited<ReturnType<typeof submitHanaViewForReview>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useSubmitHanaViewForReview<
  TData = Awaited<ReturnType<typeof submitHanaViewForReview>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: SubmitHanaViewForReviewPathParameters,
  tLHanaViewReviewerAssignmentRequest: BodyType<TLHanaViewReviewerAssignmentRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof submitHanaViewForReview>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof submitHanaViewForReview>>,
          TError,
          Awaited<ReturnType<typeof submitHanaViewForReview>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useSubmitHanaViewForReview<
  TData = Awaited<ReturnType<typeof submitHanaViewForReview>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: SubmitHanaViewForReviewPathParameters,
  tLHanaViewReviewerAssignmentRequest: BodyType<TLHanaViewReviewerAssignmentRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof submitHanaViewForReview>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Submit Hana View For Review
 */

export function useSubmitHanaViewForReview<
  TData = Awaited<ReturnType<typeof submitHanaViewForReview>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { viewId }: SubmitHanaViewForReviewPathParameters,
  tLHanaViewReviewerAssignmentRequest: BodyType<TLHanaViewReviewerAssignmentRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof submitHanaViewForReview>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getSubmitHanaViewForReviewQueryOptions({ viewId }, tLHanaViewReviewerAssignmentRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Submit Hana View For Review
 */
export const prefetchSubmitHanaViewForReviewQuery = async <
  TData = Awaited<ReturnType<typeof submitHanaViewForReview>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { viewId }: SubmitHanaViewForReviewPathParameters,
  tLHanaViewReviewerAssignmentRequest: BodyType<TLHanaViewReviewerAssignmentRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof submitHanaViewForReview>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getSubmitHanaViewForReviewQueryOptions({ viewId }, tLHanaViewReviewerAssignmentRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Submit Hana View For Review
 */
export const invalidateSubmitHanaViewForReview = async (
  queryClient: QueryClient,
  { viewId }: SubmitHanaViewForReviewPathParameters,
  tLHanaViewReviewerAssignmentRequest: BodyType<TLHanaViewReviewerAssignmentRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getSubmitHanaViewForReviewQueryKey({ viewId }, tLHanaViewReviewerAssignmentRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Approve Hana View Review
 */
export const approveHanaViewReview = (
  { viewId }: ApproveHanaViewReviewPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLHanaViewResponse>(
    { url: `/hana-views/${viewId}/reviews/approve`, method: 'POST', signal },
    options,
  );
};

export const getApproveHanaViewReviewQueryKey = ({ viewId }: ApproveHanaViewReviewPathParameters) => {
  return ['POST', 'hana-views', viewId, 'reviews', 'approve'] as const;
};

export const getApproveHanaViewReviewQueryOptions = <
  TData = Awaited<ReturnType<typeof approveHanaViewReview>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { viewId }: ApproveHanaViewReviewPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof approveHanaViewReview>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getApproveHanaViewReviewQueryKey({ viewId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof approveHanaViewReview>>> = ({ signal }) =>
    approveHanaViewReview({ viewId }, requestOptions, signal);

  return { queryKey, queryFn, enabled: viewId !== null && viewId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof approveHanaViewReview>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ApproveHanaViewReviewQueryResult = NonNullable<Awaited<ReturnType<typeof approveHanaViewReview>>>;
export type ApproveHanaViewReviewQueryError = ErrorType<TLHTTPValidationError>;

export function useApproveHanaViewReview<
  TData = Awaited<ReturnType<typeof approveHanaViewReview>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ApproveHanaViewReviewPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof approveHanaViewReview>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof approveHanaViewReview>>,
          TError,
          Awaited<ReturnType<typeof approveHanaViewReview>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useApproveHanaViewReview<
  TData = Awaited<ReturnType<typeof approveHanaViewReview>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ApproveHanaViewReviewPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof approveHanaViewReview>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof approveHanaViewReview>>,
          TError,
          Awaited<ReturnType<typeof approveHanaViewReview>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useApproveHanaViewReview<
  TData = Awaited<ReturnType<typeof approveHanaViewReview>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ApproveHanaViewReviewPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof approveHanaViewReview>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Approve Hana View Review
 */

export function useApproveHanaViewReview<
  TData = Awaited<ReturnType<typeof approveHanaViewReview>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { viewId }: ApproveHanaViewReviewPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof approveHanaViewReview>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getApproveHanaViewReviewQueryOptions({ viewId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Approve Hana View Review
 */
export const prefetchApproveHanaViewReviewQuery = async <
  TData = Awaited<ReturnType<typeof approveHanaViewReview>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { viewId }: ApproveHanaViewReviewPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof approveHanaViewReview>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getApproveHanaViewReviewQueryOptions({ viewId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Approve Hana View Review
 */
export const invalidateApproveHanaViewReview = async (
  queryClient: QueryClient,
  { viewId }: ApproveHanaViewReviewPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getApproveHanaViewReviewQueryKey({ viewId }) }, options);

  return queryClient;
};

/**
 * @summary Reject Hana View Review
 */
export const rejectHanaViewReview = (
  { viewId }: RejectHanaViewReviewPathParameters,
  tLHanaViewReviewRejectRequest: BodyType<TLHanaViewReviewRejectRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLHanaViewResponse>(
    {
      url: `/hana-views/${viewId}/reviews/reject`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLHanaViewReviewRejectRequest,
      signal,
    },
    options,
  );
};

export const getRejectHanaViewReviewQueryKey = (
  { viewId }: RejectHanaViewReviewPathParameters,
  tLHanaViewReviewRejectRequest?: BodyType<TLHanaViewReviewRejectRequest>,
) => {
  return ['POST', 'hana-views', viewId, 'reviews', 'reject', tLHanaViewReviewRejectRequest] as const;
};

export const getRejectHanaViewReviewQueryOptions = <
  TData = Awaited<ReturnType<typeof rejectHanaViewReview>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { viewId }: RejectHanaViewReviewPathParameters,
  tLHanaViewReviewRejectRequest: BodyType<TLHanaViewReviewRejectRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof rejectHanaViewReview>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getRejectHanaViewReviewQueryKey({ viewId }, tLHanaViewReviewRejectRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof rejectHanaViewReview>>> = ({ signal }) =>
    rejectHanaViewReview({ viewId }, tLHanaViewReviewRejectRequest, requestOptions, signal);

  return { queryKey, queryFn, enabled: viewId !== null && viewId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof rejectHanaViewReview>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type RejectHanaViewReviewQueryResult = NonNullable<Awaited<ReturnType<typeof rejectHanaViewReview>>>;
export type RejectHanaViewReviewQueryError = ErrorType<TLHTTPValidationError>;

export function useRejectHanaViewReview<
  TData = Awaited<ReturnType<typeof rejectHanaViewReview>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RejectHanaViewReviewPathParameters,
  tLHanaViewReviewRejectRequest: BodyType<TLHanaViewReviewRejectRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof rejectHanaViewReview>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof rejectHanaViewReview>>,
          TError,
          Awaited<ReturnType<typeof rejectHanaViewReview>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useRejectHanaViewReview<
  TData = Awaited<ReturnType<typeof rejectHanaViewReview>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RejectHanaViewReviewPathParameters,
  tLHanaViewReviewRejectRequest: BodyType<TLHanaViewReviewRejectRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof rejectHanaViewReview>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof rejectHanaViewReview>>,
          TError,
          Awaited<ReturnType<typeof rejectHanaViewReview>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useRejectHanaViewReview<
  TData = Awaited<ReturnType<typeof rejectHanaViewReview>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RejectHanaViewReviewPathParameters,
  tLHanaViewReviewRejectRequest: BodyType<TLHanaViewReviewRejectRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof rejectHanaViewReview>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Reject Hana View Review
 */

export function useRejectHanaViewReview<
  TData = Awaited<ReturnType<typeof rejectHanaViewReview>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { viewId }: RejectHanaViewReviewPathParameters,
  tLHanaViewReviewRejectRequest: BodyType<TLHanaViewReviewRejectRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof rejectHanaViewReview>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getRejectHanaViewReviewQueryOptions({ viewId }, tLHanaViewReviewRejectRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Reject Hana View Review
 */
export const prefetchRejectHanaViewReviewQuery = async <
  TData = Awaited<ReturnType<typeof rejectHanaViewReview>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { viewId }: RejectHanaViewReviewPathParameters,
  tLHanaViewReviewRejectRequest: BodyType<TLHanaViewReviewRejectRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof rejectHanaViewReview>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getRejectHanaViewReviewQueryOptions({ viewId }, tLHanaViewReviewRejectRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Reject Hana View Review
 */
export const invalidateRejectHanaViewReview = async (
  queryClient: QueryClient,
  { viewId }: RejectHanaViewReviewPathParameters,
  tLHanaViewReviewRejectRequest: BodyType<TLHanaViewReviewRejectRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getRejectHanaViewReviewQueryKey({ viewId }, tLHanaViewReviewRejectRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Start Hana View
 */
export const startHanaView = (
  { viewId }: StartHanaViewPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLHanaViewResponse>({ url: `/hana-views/${viewId}/start`, method: 'POST', signal }, options);
};

export const getStartHanaViewQueryKey = ({ viewId }: StartHanaViewPathParameters) => {
  return ['POST', 'hana-views', viewId, 'start'] as const;
};

export const getStartHanaViewQueryOptions = <
  TData = Awaited<ReturnType<typeof startHanaView>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { viewId }: StartHanaViewPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof startHanaView>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getStartHanaViewQueryKey({ viewId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof startHanaView>>> = ({ signal }) =>
    startHanaView({ viewId }, requestOptions, signal);

  return { queryKey, queryFn, enabled: viewId !== null && viewId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof startHanaView>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type StartHanaViewQueryResult = NonNullable<Awaited<ReturnType<typeof startHanaView>>>;
export type StartHanaViewQueryError = ErrorType<TLHTTPValidationError>;

export function useStartHanaView<
  TData = Awaited<ReturnType<typeof startHanaView>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: StartHanaViewPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof startHanaView>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof startHanaView>>,
          TError,
          Awaited<ReturnType<typeof startHanaView>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useStartHanaView<
  TData = Awaited<ReturnType<typeof startHanaView>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: StartHanaViewPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof startHanaView>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof startHanaView>>,
          TError,
          Awaited<ReturnType<typeof startHanaView>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useStartHanaView<
  TData = Awaited<ReturnType<typeof startHanaView>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: StartHanaViewPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof startHanaView>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Start Hana View
 */

export function useStartHanaView<
  TData = Awaited<ReturnType<typeof startHanaView>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { viewId }: StartHanaViewPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof startHanaView>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getStartHanaViewQueryOptions({ viewId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Start Hana View
 */
export const prefetchStartHanaViewQuery = async <
  TData = Awaited<ReturnType<typeof startHanaView>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { viewId }: StartHanaViewPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof startHanaView>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getStartHanaViewQueryOptions({ viewId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Start Hana View
 */
export const invalidateStartHanaView = async (
  queryClient: QueryClient,
  { viewId }: StartHanaViewPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getStartHanaViewQueryKey({ viewId }) }, options);

  return queryClient;
};

/**
 * @summary List Hana View Versions
 */
export const listHanaViewVersions = (
  { viewId }: ListHanaViewVersionsPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLHanaViewVersionResponse[]>(
    { url: `/hana-views/${viewId}/versions`, method: 'GET', signal },
    options,
  );
};

export const getListHanaViewVersionsQueryKey = ({ viewId }: ListHanaViewVersionsPathParameters) => {
  return ['hana-views', viewId, 'versions'] as const;
};

export const getListHanaViewVersionsQueryOptions = <
  TData = Awaited<ReturnType<typeof listHanaViewVersions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { viewId }: ListHanaViewVersionsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listHanaViewVersions>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListHanaViewVersionsQueryKey({ viewId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listHanaViewVersions>>> = ({ signal }) =>
    listHanaViewVersions({ viewId }, requestOptions, signal);

  return { queryKey, queryFn, enabled: viewId !== null && viewId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listHanaViewVersions>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListHanaViewVersionsQueryResult = NonNullable<Awaited<ReturnType<typeof listHanaViewVersions>>>;
export type ListHanaViewVersionsQueryError = ErrorType<TLHTTPValidationError>;

export function useListHanaViewVersions<
  TData = Awaited<ReturnType<typeof listHanaViewVersions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ListHanaViewVersionsPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof listHanaViewVersions>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listHanaViewVersions>>,
          TError,
          Awaited<ReturnType<typeof listHanaViewVersions>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListHanaViewVersions<
  TData = Awaited<ReturnType<typeof listHanaViewVersions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ListHanaViewVersionsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listHanaViewVersions>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listHanaViewVersions>>,
          TError,
          Awaited<ReturnType<typeof listHanaViewVersions>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListHanaViewVersions<
  TData = Awaited<ReturnType<typeof listHanaViewVersions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ListHanaViewVersionsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listHanaViewVersions>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List Hana View Versions
 */

export function useListHanaViewVersions<
  TData = Awaited<ReturnType<typeof listHanaViewVersions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { viewId }: ListHanaViewVersionsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listHanaViewVersions>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListHanaViewVersionsQueryOptions({ viewId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary List Hana View Versions
 */
export const prefetchListHanaViewVersionsQuery = async <
  TData = Awaited<ReturnType<typeof listHanaViewVersions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { viewId }: ListHanaViewVersionsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listHanaViewVersions>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getListHanaViewVersionsQueryOptions({ viewId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary List Hana View Versions
 */
export const invalidateListHanaViewVersions = async (
  queryClient: QueryClient,
  { viewId }: ListHanaViewVersionsPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getListHanaViewVersionsQueryKey({ viewId }) }, options);

  return queryClient;
};

/**
 * @summary Get Hana View Xml
 */
export const getHanaViewXml = (
  { viewId }: GetHanaViewXmlPathParameters,
  params?: GetHanaViewXmlParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLHanaViewXmlResponse>(
    { url: `/hana-views/${viewId}/xml`, method: 'GET', params, signal },
    options,
  );
};

export const getGetHanaViewXmlQueryKey = ({ viewId }: GetHanaViewXmlPathParameters, params?: GetHanaViewXmlParams) => {
  return ['hana-views', viewId, 'xml', ...(params ? [params] : [])] as const;
};

export const getGetHanaViewXmlQueryOptions = <
  TData = Awaited<ReturnType<typeof getHanaViewXml>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { viewId }: GetHanaViewXmlPathParameters,
  params?: GetHanaViewXmlParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getHanaViewXml>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetHanaViewXmlQueryKey({ viewId }, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getHanaViewXml>>> = ({ signal }) =>
    getHanaViewXml({ viewId }, params, requestOptions, signal);

  return { queryKey, queryFn, enabled: viewId !== null && viewId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getHanaViewXml>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetHanaViewXmlQueryResult = NonNullable<Awaited<ReturnType<typeof getHanaViewXml>>>;
export type GetHanaViewXmlQueryError = ErrorType<TLHTTPValidationError>;

export function useGetHanaViewXml<
  TData = Awaited<ReturnType<typeof getHanaViewXml>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetHanaViewXmlPathParameters,
  params: undefined | GetHanaViewXmlParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getHanaViewXml>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getHanaViewXml>>,
          TError,
          Awaited<ReturnType<typeof getHanaViewXml>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetHanaViewXml<
  TData = Awaited<ReturnType<typeof getHanaViewXml>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetHanaViewXmlPathParameters,
  params?: GetHanaViewXmlParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getHanaViewXml>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getHanaViewXml>>,
          TError,
          Awaited<ReturnType<typeof getHanaViewXml>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetHanaViewXml<
  TData = Awaited<ReturnType<typeof getHanaViewXml>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetHanaViewXmlPathParameters,
  params?: GetHanaViewXmlParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getHanaViewXml>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Hana View Xml
 */

export function useGetHanaViewXml<
  TData = Awaited<ReturnType<typeof getHanaViewXml>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { viewId }: GetHanaViewXmlPathParameters,
  params?: GetHanaViewXmlParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getHanaViewXml>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetHanaViewXmlQueryOptions({ viewId }, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Hana View Xml
 */
export const prefetchGetHanaViewXmlQuery = async <
  TData = Awaited<ReturnType<typeof getHanaViewXml>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { viewId }: GetHanaViewXmlPathParameters,
  params?: GetHanaViewXmlParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getHanaViewXml>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetHanaViewXmlQueryOptions({ viewId }, params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Hana View Xml
 */
export const invalidateGetHanaViewXml = async (
  queryClient: QueryClient,
  { viewId }: GetHanaViewXmlPathParameters,
  params?: GetHanaViewXmlParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetHanaViewXmlQueryKey({ viewId }, params) }, options);

  return queryClient;
};
